import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { RJSFSchema } from '@rjsf/utils';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';

import preparePropertyFormSchema from '~/components/Vendors/Properties/helpers/preparePropertyFormSchema';

import { useGetAmenities } from '~/hooks/apis/accommodation/useGetAmenities';
import { useGetPropertiesSchema } from '~/hooks/apis/accommodation/useGetProperty';
import { useRoomTypesSchema } from '~/hooks/apis/accommodation/useRoomTypesSchema';
import { useSaveOfferBuilderRoom } from '~/hooks/apis/accommodation/useSaveOfferBuilderRoom';
import { useGenerateBulkBeddingConfigurations } from '~/hooks/apis/offers/useGenerateBeddingConfiguration';
import { useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';
import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';
import { useCreateBatchRoomRates } from '~/hooks/apis/reservation/useCreateBatchRoomRates';
import { useGetRatePlanCreateSchema } from '~/hooks/apis/reservation/usePropertyRatePlans';

import OffersService from '~/services/OffersService';
import PackagesService from '~/services/PackagesService';
import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';
import { mapSFCancellationPolicyToAdmin } from '~/utils/mapSFCancelltionPolicyToAdmin';
import { reportError } from '~/utils/reportError';
import { isSalesforceOpportunityId } from '~/utils/validateSalesforceId';

import ExitConfirmationDialog from './ExitConfirmationDialog';
import PropertyStep from './PropertyStep';
import RatePlanStep from './RatePlanStep';
import {
  FLASH_RATE_PLAN_PRODUCT_TYPE,
  buildCreatedRatePlansFromDrafts,
  buildDefaultRatePlanDraft,
  getDefaultFlashRatePlanName,
  getRatePlanDraftsValidationError,
  isCreatedRatePlanDraft,
} from './RatePlanStep/utils';
import type { FlashRatePlanDraft } from './RatePlanStep/utils';
import RoomRatesStep from './RoomRatesStep';
import { buildRoomRateRooms } from './RoomRatesStep/utils';
import RoomsStep from './RoomsStep';
import {
  buildBlankRoomDraft,
  buildRoomCreatePayload,
  getCreatableRoomDrafts,
  getPropertyRoomTypes,
  getSalesforceRoomsMissingExistingRoomTypes,
  mapGeneratedBeddingConfigurationToBedGroups,
  mapSalesforceRoomsToRoomDrafts,
} from './RoomsStep/utils';
import type { FlashOfferRoomDraft } from './RoomsStep/utils';
import TaxesFeesStep from './TaxesFeesStep';
import WizardActions from './WizardActions';
import { CREATE_NEW_PROPERTY, DEFAULT_NEW_PROPERTY_LOGO_ID, STEPS, TAXES_FEES_FORM_ID } from './constants';
import type { AppliedFlashRoomRate, FlashOfferProperty, SetUpFlashOfferSectionProps } from './types';
import {
  buildFlashPackageRoomRateAssignments,
  getFlashRatePlanNameFromTravelStart,
  getMissingRequiredFieldLabels,
  getMissingRequiredPropertyFields,
  getNextButtonLabel,
  mapExtraGuestSurchargeAgeRangesToPropertyFormData,
  mapRedemptionOptionsToPropertyFormData,
  mapVendorBillingToPropertyFormData,
} from './utils';

export default function SetUpFlashOfferSection({
  disabled,
  onPropertiesRefresh,
  properties = [],
  vendor,
  vendorId,
}: SetUpFlashOfferSectionProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const defaultPropertyId = properties.find((property) => !property.deleted_at)?.id ?? CREATE_NEW_PROPERTY;
  const [salesforceOfferId, setSalesforceOfferId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isExitConfirmationOpen, setIsExitConfirmationOpen] = useState(false);
  const [isCreatingNewProperty, setIsCreatingNewProperty] = useState(false);
  const [isCreatingRatePlan, setIsCreatingRatePlan] = useState(false);
  const [isCreatingRooms, setIsCreatingRooms] = useState(false);
  const [isCreatingRoomRates, setIsCreatingRoomRates] = useState(false);
  const [hasAppliedRoomRates, setHasAppliedRoomRates] = useState(false);
  const [isValidatingOpportunity, setIsValidatingOpportunity] = useState(false);
  const [isAdvancingStep, setIsAdvancingStep] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPropertyId, setSelectedPropertyId] = useState(defaultPropertyId);
  const [createdProperty, setCreatedProperty] = useState<FlashOfferProperty | null>(null);
  const [createdRatePlans, setCreatedRatePlans] = useState<Array<App.RatePlan>>([]);
  const [ratePlanDrafts, setRatePlanDrafts] = useState<Array<FlashRatePlanDraft>>([]);
  const [expandedRatePlanDraftIds, setExpandedRatePlanDraftIds] = useState<Array<string>>([]);
  const [additionalAmenityOptions, setAdditionalAmenityOptions] = useState<Array<string>>([]);
  const [salesforceRooms, setSalesforceRooms] = useState<Array<SalesforceRoom>>([]);
  const [propertyFormData, setPropertyFormData] = useState<Record<string, unknown>>({});
  const [roomDrafts, setRoomDrafts] = useState<Array<FlashOfferRoomDraft>>([]);
  const [appliedFlashRoomRates, setAppliedFlashRoomRates] = useState<Array<AppliedFlashRoomRate>>([]);
  const [selectedRoomRatePlanIds, setSelectedRoomRatePlanIds] = useState<Record<string, Array<string>>>({});
  const [isSavingTaxesFees, setIsSavingTaxesFees] = useState(false);
  const validationRunId = useRef(0);
  const manualRoomDraftCounter = useRef(0);
  const ratePlanDraftCounter = useRef(0);
  const defaultRoomRateSelectionKey = useRef<string | null>(null);
  const defaultRatePlanDraftValues = useRef({
    ratePlanCancellationPolicy: '',
    ratePlanName: getDefaultFlashRatePlanName(),
  });
  const trimmedSalesforceOfferId = salesforceOfferId.trim();
  const contentPageUrl = trimmedSalesforceOfferId
    ? `/offers/${trimmedSalesforceOfferId}/offer-builder/cm-checklist`
    : '';
  const isCreatingProperty = selectedPropertyId === CREATE_NEW_PROPERTY;
  const isPropertyStep = activeStep === 1;
  const isRoomStep = activeStep === 2;
  const isRatePlanStep = activeStep === 3;
  const isRoomRatesStep = activeStep === 4;
  const isTaxesAndFeesStep = activeStep === 5;
  const isContentStep = activeStep === STEPS.length - 1;
  const isCreatePropertyStep = isPropertyStep && isCreatingProperty;
  const { refetch: refetchOpportunity } = useGetOpportunity(trimmedSalesforceOfferId);
  const { refetch: refetchOpportunityRelated } = useGetOpportunityRelated(trimmedSalesforceOfferId, { enabled: false });
  const saveRoom = useSaveOfferBuilderRoom();
  const createBatchRoomRates = useCreateBatchRoomRates();
  const generateBulkBeddingConfigurations = useGenerateBulkBeddingConfigurations();
  const {
    data: createPropertySchema,
    isError: hasCreatePropertySchemaError,
    isFetching: isLoadingCreatePropertySchema,
    refetch: refetchCreatePropertySchema,
  } = useGetPropertiesSchema(activeStep === 1 && isCreatingProperty);
  const {
    data: ratePlanCreateSchema,
    isError: hasRatePlanCreateSchemaError,
    isFetching: isLoadingRatePlanCreateSchema,
    refetch: refetchRatePlanCreateSchema,
  } = useGetRatePlanCreateSchema(isRatePlanStep);
  const createdRatePlanIds = useMemo(
    () => createdRatePlans.map((ratePlan) => ratePlan.id).filter(Boolean),
    [createdRatePlans],
  );
  const areRatePlanDraftsComplete = ratePlanDrafts.length > 0 && !getRatePlanDraftsValidationError(ratePlanDrafts);
  const isRatePlanStepReadOnly =
    ratePlanDrafts.length > 0 && ratePlanDrafts.every((draft) => !!draft.createdRatePlanId);
  const isRoomRatesStepReadOnly = hasAppliedRoomRates;
  const appliedRatePlanNamesByRoomTypeId = useMemo(
    () =>
      appliedFlashRoomRates.reduce<Record<string, Array<string>>>((acc, roomRate) => {
        const label = roomRate.ratePlanName || roomRate.ratePlanId;
        const existingLabels = acc[roomRate.roomTypeId] ?? [];

        if (!existingLabels.includes(label)) {
          acc[roomRate.roomTypeId] = existingLabels.concat(label);
        }

        return acc;
      }, {}),
    [appliedFlashRoomRates],
  );
  const currentStep = STEPS[activeStep];
  const selectableProperties =
    !createdProperty || properties.some((property) => property.id === createdProperty.id)
      ? properties
      : [...properties, createdProperty];
  const activeProperties = selectableProperties.filter((property) => !property.deleted_at);
  const selectedProperty = selectableProperties.find((property) => property.id === selectedPropertyId) ?? null;
  const selectedPropertyRoomTypes = useMemo(() => getPropertyRoomTypes(selectedProperty), [selectedProperty]);
  const selectedPropertyHasRooms = selectedPropertyRoomTypes.length > 0;
  const roomDraftsToCreate = getCreatableRoomDrafts(roomDrafts);
  const appliedRoomRateRoomTypeIds = useMemo(
    () => Array.from(new Set(appliedFlashRoomRates.map((roomRate) => roomRate.roomTypeId))),
    [appliedFlashRoomRates],
  );
  const roomRateRooms = useMemo(
    () =>
      buildRoomRateRooms({
        appliedRoomTypeIds: appliedRoomRateRoomTypeIds,
        ratePlanIds: createdRatePlanIds,
        roomDrafts,
        roomTypes: selectedPropertyRoomTypes,
      }),
    [appliedRoomRateRoomTypeIds, createdRatePlanIds, roomDrafts, selectedPropertyRoomTypes],
  );
  const selectableRoomRateRoomTypeIds = useMemo(
    () => roomRateRooms.filter((room) => !room.hasRatePlan).map((room) => room.id),
    [roomRateRooms],
  );
  const selectedPropertyIdForRooms = selectedProperty?.id;
  const {
    data: roomTypesSchema,
    isError: hasRoomTypesSchemaError,
    isFetching: isLoadingRoomTypesSchema,
    refetch: refetchRoomTypesSchema,
  } = useRoomTypesSchema({
    propertyId: selectedPropertyIdForRooms,
    enabled: isRoomStep && !!selectedPropertyIdForRooms,
  });
  const { data: amenityOptionsData = [], isFetching: isLoadingAmenities } = useGetAmenities({ enabled: isRoomStep });
  const roomTypesFormSchema = useMemo<RJSFSchema | undefined>(
    () => (roomTypesSchema?.post?.body?.schema ?? roomTypesSchema?.put?.body?.schema) as RJSFSchema | undefined,
    [roomTypesSchema],
  );
  const amenityOptions = useMemo(() => {
    const fetchedAmenityOptions = (amenityOptionsData as Array<{ name?: string | null }>)
      .map((item) => item.name)
      .filter((name): name is string => typeof name === 'string' && !!name.trim());

    return Array.from(new Set<string>(fetchedAmenityOptions.concat(additionalAmenityOptions))).sort();
  }, [additionalAmenityOptions, amenityOptionsData]);
  const missingRequiredRoomFields = roomDraftsToCreate.flatMap((roomDraft, index) =>
    getMissingRequiredFieldLabels(roomTypesFormSchema, roomDraft).map(
      (fieldLabel) => `Room ${index + 1}: ${fieldLabel}`,
    ),
  );
  const hasInvalidRoomDraft = roomTypesFormSchema
    ? missingRequiredRoomFields.length > 0
    : roomDraftsToCreate.some((roomDraft) => !roomDraft.name.trim());
  const isLoadingRoomDraftForm =
    isRoomStep && roomDrafts.length > 0 && (isLoadingRoomTypesSchema || isLoadingAmenities);
  const hasRoomDraftFormError =
    isRoomStep &&
    roomDrafts.length > 0 &&
    !isLoadingRoomTypesSchema &&
    (hasRoomTypesSchemaError || !roomTypesFormSchema);
  const propertySchema = useMemo(
    () =>
      preparePropertyFormSchema(createPropertySchema, {
        type: propertyFormData.type,
        channel_manager: propertyFormData.channel_manager,
      }),
    [createPropertySchema, propertyFormData.type, propertyFormData.channel_manager],
  );
  const isLoadingPropertyForm = isCreatingProperty && isLoadingCreatePropertySchema;
  const hasPropertyFormError = isCreatingProperty && hasCreatePropertySchemaError;
  const missingRequiredPropertyFields = getMissingRequiredPropertyFields({
    isCreatePropertyStep,
    propertyFormData,
    propertySchema,
  });
  const hasMissingRequiredPropertyFields = missingRequiredPropertyFields.length > 0;
  const isPropertyStepNextDisabled =
    isCreatePropertyStep &&
    (isLoadingPropertyForm || hasPropertyFormError || !propertySchema || hasMissingRequiredPropertyFields);
  const isRatePlanStepNextDisabled =
    isRatePlanStep &&
    (isCreatingRatePlan || !areRatePlanDraftsComplete || isLoadingRatePlanCreateSchema || hasRatePlanCreateSchemaError);
  const isRoomStepNextDisabled =
    isRoomStep && (isCreatingRooms || isLoadingRoomDraftForm || hasRoomDraftFormError || hasInvalidRoomDraft);
  const isRoomRatesStepNextDisabled =
    isRoomRatesStep &&
    !hasAppliedRoomRates &&
    (createdRatePlans.length === 0 ||
      isCreatingRoomRates ||
      Object.values(selectedRoomRatePlanIds).every((ratePlanIds) => ratePlanIds.length === 0));
  const isStepBusy =
    isAdvancingStep ||
    isValidatingOpportunity ||
    isCreatingNewProperty ||
    isCreatingRooms ||
    isCreatingRatePlan ||
    isCreatingRoomRates ||
    isSavingTaxesFees;
  const nextButtonLabel = getNextButtonLabel({
    isAdvancingStep,
    isCreatePropertyStep,
    isCreatingNewProperty,
    isCreatingRooms,
    isRoomStep,
    roomCreateCount: roomDraftsToCreate.length,
    isValidatingOpportunity,
  });
  const primaryButtonLabel = isRatePlanStep
    ? isCreatingRatePlan
      ? 'Creating...'
      : nextButtonLabel
    : isRoomRatesStep
      ? hasAppliedRoomRates
        ? nextButtonLabel
        : isCreatingRoomRates
          ? 'Applying...'
          : 'Apply rate plans'
      : isTaxesAndFeesStep
        ? isSavingTaxesFees
          ? 'Saving...'
          : 'Save'
        : nextButtonLabel;

  useEffect(() => {
    if (
      isRoomRatesStep &&
      createdRatePlans.length > 0 &&
      roomRateRooms.length > 0 &&
      selectableRoomRateRoomTypeIds.length === 0 &&
      !hasAppliedRoomRates
    ) {
      setHasAppliedRoomRates(true);
    }
  }, [
    createdRatePlans.length,
    hasAppliedRoomRates,
    isRoomRatesStep,
    roomRateRooms.length,
    selectableRoomRateRoomTypeIds.length,
  ]);

  useEffect(() => {
    if (
      !isRoomRatesStep ||
      hasAppliedRoomRates ||
      createdRatePlans.length === 0 ||
      selectableRoomRateRoomTypeIds.length === 0
    ) {
      return;
    }

    const selectionKey = createdRatePlanIds.join(',');

    if (defaultRoomRateSelectionKey.current === selectionKey) {
      return;
    }

    defaultRoomRateSelectionKey.current = selectionKey;
    const defaultRatePlanIds = createdRatePlanIds;
    setSelectedRoomRatePlanIds(
      defaultRatePlanIds.length > 0
        ? Object.fromEntries(selectableRoomRateRoomTypeIds.map((roomTypeId) => [roomTypeId, defaultRatePlanIds]))
        : {},
    );
  }, [
    createdRatePlanIds,
    createdRatePlans.length,
    hasAppliedRoomRates,
    isRoomRatesStep,
    selectableRoomRateRoomTypeIds,
  ]);

  useEffect(() => {
    if (isDialogOpen && isContentStep && contentPageUrl) {
      navigate(contentPageUrl);
    }
  }, [contentPageUrl, isContentStep, isDialogOpen, navigate]);

  function goToContentPage() {
    if (contentPageUrl) {
      navigate(contentPageUrl);
    }
  }

  function handleOpen() {
    setIsDialogOpen(true);
    setIsExitConfirmationOpen(false);
    setIsCreatingNewProperty(false);
    setIsCreatingRatePlan(false);
    setIsCreatingRooms(false);
    setIsCreatingRoomRates(false);
    setIsSavingTaxesFees(false);
    setHasAppliedRoomRates(false);
    setIsValidatingOpportunity(false);
    setIsAdvancingStep(false);
    validationRunId.current += 1;
    manualRoomDraftCounter.current = 0;
    ratePlanDraftCounter.current = 0;
    defaultRoomRateSelectionKey.current = null;
    setSalesforceOfferId('');
    setErrorMessage(null);
    setActiveStep(0);
    setSelectedPropertyId(defaultPropertyId);
    setCreatedProperty(null);
    setCreatedRatePlans([]);
    initializeRatePlanDrafts();
    setAdditionalAmenityOptions([]);
    setSalesforceRooms([]);
    setPropertyFormData({});
    setRoomDrafts([]);
    setAppliedFlashRoomRates([]);
    setSelectedRoomRatePlanIds({});
  }

  function handleClose() {
    if (isStepBusy) {
      return;
    }

    if (activeStep === 0 && !trimmedSalesforceOfferId) {
      handleConfirmClose();
      return;
    }

    setIsExitConfirmationOpen(true);
  }

  function handleCancelClose() {
    setIsExitConfirmationOpen(false);
  }

  function handleConfirmClose() {
    validationRunId.current += 1;
    setIsCreatingNewProperty(false);
    setIsCreatingRatePlan(false);
    setIsCreatingRooms(false);
    setIsCreatingRoomRates(false);
    setIsSavingTaxesFees(false);
    setHasAppliedRoomRates(false);
    setIsValidatingOpportunity(false);
    setIsAdvancingStep(false);
    setIsExitConfirmationOpen(false);
    setIsDialogOpen(false);
  }

  function handlePrevious() {
    setErrorMessage(null);
    setActiveStep((prev) => (prev === 3 && selectedPropertyHasRooms ? 1 : Math.max(prev - 1, 0)));
  }

  function handlePropertyCreateStart() {
    setErrorMessage(null);
    setIsCreatingNewProperty(true);
  }

  function handlePropertyCreateError() {
    setIsCreatingNewProperty(false);
    setErrorMessage('Unable to create property.');
  }

  async function getRoomsWithBeddingConfigurations(rooms: Array<SalesforceRoom>) {
    const bedGroupsByDealId = new Map<string, SalesforceRoom['bed_groups']>();
    const dealIdByRoomAssetId = new Map<string, string>();
    let hasBeddingConfigurationError = false;

    rooms.forEach((room) => {
      const roomAssetId = room.room_asset?.sfid || room.room_asset?.id;
      const roomAssetKey = roomAssetId ? String(roomAssetId) : '';
      const dealId = room.id ? String(room.id) : '';

      if (roomAssetKey && dealId && !dealIdByRoomAssetId.has(roomAssetKey)) {
        dealIdByRoomAssetId.set(roomAssetKey, dealId);
      }
    });

    const dealIds = Array.from(dealIdByRoomAssetId.values());

    for (let index = 0; index < dealIds.length; index += 10) {
      let response;

      try {
        response = await generateBulkBeddingConfigurations.mutateAsync(dealIds.slice(index, index + 10));
      } catch (error) {
        reportError(error);
        hasBeddingConfigurationError = true;
        continue;
      }

      response.result?.forEach((result) => {
        if (result.status !== 200) {
          hasBeddingConfigurationError = true;
          return;
        }

        const bedGroups = mapGeneratedBeddingConfigurationToBedGroups({ result: result.result });

        if (bedGroups.length > 0) {
          bedGroupsByDealId.set(result.deal_offer_id, bedGroups);
        }
      });
    }

    if (hasBeddingConfigurationError) {
      setErrorMessage('Some bedding configurations could not be prefilled. Review room bedding before continuing.');
    }

    return rooms.map((room) => {
      const bedGroups = bedGroupsByDealId.get(String(room.id));
      return bedGroups ? { ...room, bed_groups: bedGroups } : room;
    });
  }

  async function prepareRoomDrafts(property: FlashOfferProperty | null, existingRoomTypes: Array<App.RoomType> = []) {
    if (!property?.id) {
      setRoomDrafts([]);
      return [];
    }

    const missingSalesforceRooms = getSalesforceRoomsMissingExistingRoomTypes(salesforceRooms, existingRoomTypes);

    if (missingSalesforceRooms.length === 0) {
      setRoomDrafts([]);
      return [];
    }

    const rooms = await getRoomsWithBeddingConfigurations(missingSalesforceRooms);
    const roomDrafts = mapSalesforceRoomsToRoomDrafts({
      propertyCategory: property.category,
      propertyId: property.id,
      rooms,
    });

    setRoomDrafts(roomDrafts);
    return roomDrafts;
  }

  async function handlePropertyCreateSuccess(property: FlashOfferProperty) {
    if (!property?.id) {
      handlePropertyCreateError();
      return;
    }

    setCreatedProperty(property);
    setSelectedPropertyId(property.id);
    setErrorMessage(null);
    setIsAdvancingStep(true);

    try {
      await prepareRoomDrafts(property);
      await refreshProperties(property);
      setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    } finally {
      setIsAdvancingStep(false);
      setIsCreatingNewProperty(false);
    }
  }

  function updateRoomDraft(draftId: string, updates: Partial<FlashOfferRoomDraft>) {
    setRoomDrafts((prev) =>
      prev.map((roomDraft) => (roomDraft.draftId === draftId ? { ...roomDraft, ...updates } : roomDraft)),
    );
  }

  function handleAddAmenityOption(amenityName: string) {
    const trimmedAmenityName = amenityName.trim();

    if (!trimmedAmenityName) {
      return;
    }

    setAdditionalAmenityOptions((prev) => (prev.includes(trimmedAmenityName) ? prev : prev.concat(trimmedAmenityName)));
  }

  function handleAddRoomDraft() {
    const propertyId = selectedProperty?.id;

    if (!propertyId) {
      setErrorMessage('Select or create a property before adding rooms.');
      return;
    }

    manualRoomDraftCounter.current += 1;
    setRoomDrafts((prev) => [
      ...prev,
      buildBlankRoomDraft({
        draftId: `manual-room-${manualRoomDraftCounter.current}`,
        propertyCategory: selectedProperty.category,
        propertyId,
      }),
    ]);
  }

  function handleRemoveRoomDraft(draftId: string) {
    setRoomDrafts((prev) => prev.filter((roomDraft) => roomDraft.draftId !== draftId));
  }

  function markRoomDraftsCreated(savedRoomsByDraftId: Map<string, App.RoomType>) {
    setRoomDrafts((prev) =>
      prev.map((roomDraft) => {
        const savedRoom = savedRoomsByDraftId.get(roomDraft.draftId);

        if (!savedRoom) {
          return roomDraft;
        }

        return {
          ...roomDraft,
          id: savedRoom.id,
          existingRoomName: savedRoom.name || roomDraft.name,
          shouldCreate: false,
        };
      }),
    );
  }

  async function createSelectedRooms() {
    const propertyId = selectedProperty?.id;

    if (!propertyId) {
      setErrorMessage('Select or create a property before creating rooms.');
      return false;
    }

    if (hasRoomDraftFormError) {
      setErrorMessage('Unable to load room fields.');
      return false;
    }

    if (hasInvalidRoomDraft) {
      setErrorMessage(`Complete mandatory room fields: ${missingRequiredRoomFields.join(', ') || 'Name'}.`);
      return false;
    }

    if (roomDraftsToCreate.length === 0) {
      return true;
    }

    const savedRoomsByDraftId = new Map<string, App.RoomType>();

    setIsCreatingRooms(true);
    setErrorMessage(null);

    try {
      for (const roomDraft of roomDraftsToCreate) {
        const savedRoom = await saveRoom.mutateAsync({
          offerId: trimmedSalesforceOfferId,
          propertyId,
          room: buildRoomCreatePayload(roomDraft, propertyId),
          suppressNotifications: true,
        });
        savedRoomsByDraftId.set(roomDraft.draftId, savedRoom);
      }

      markRoomDraftsCreated(savedRoomsByDraftId);
      await refreshProperties(selectedProperty ?? undefined, Array.from(savedRoomsByDraftId.values()));
      return true;
    } catch (error) {
      markRoomDraftsCreated(savedRoomsByDraftId);
      setErrorMessage(`Unable to create rooms. ${getErrorMessage(error)}`);
      return false;
    } finally {
      setIsCreatingRooms(false);
    }
  }

  async function refreshProperties(property?: FlashOfferProperty, roomTypes?: Array<App.RoomType>) {
    try {
      await onPropertiesRefresh?.(property, roomTypes);
    } catch (error) {
      reportError(error);
    }
  }

  function generateRatePlanDraftId() {
    ratePlanDraftCounter.current += 1;
    return `rate-plan-draft-${ratePlanDraftCounter.current}`;
  }

  const initializeRatePlanDrafts = useCallback(
    ({
      ratePlanCancellationPolicy = '',
      ratePlanName = getDefaultFlashRatePlanName(),
    }: {
      ratePlanCancellationPolicy?: string;
      ratePlanName?: string;
    } = {}) => {
      defaultRatePlanDraftValues.current = {
        ratePlanCancellationPolicy,
        ratePlanName,
      };

      const draft = buildDefaultRatePlanDraft({
        draftId: generateRatePlanDraftId(),
        ratePlanCancellationPolicy,
        ratePlanName,
      });

      setRatePlanDrafts([draft]);
      setExpandedRatePlanDraftIds([draft.draftId]);
      setCreatedRatePlans([]);
    },
    [],
  );

  useEffect(() => {
    if (isRatePlanStep && ratePlanDrafts.length === 0) {
      initializeRatePlanDrafts();
    }
  }, [initializeRatePlanDrafts, isRatePlanStep, ratePlanDrafts.length]);

  function updateRatePlanDraft(draftId: string, updates: Partial<FlashRatePlanDraft>) {
    if (isRatePlanStepReadOnly) {
      return;
    }

    setRatePlanDrafts((prev) =>
      prev.map((draft) => {
        if (draft.draftId !== draftId || isCreatedRatePlanDraft(draft)) {
          return draft;
        }

        return { ...draft, ...updates };
      }),
    );
  }

  function addRatePlanDraft() {
    if (isRatePlanStepReadOnly) {
      return;
    }

    const { ratePlanCancellationPolicy, ratePlanName } = defaultRatePlanDraftValues.current;
    const draftId = generateRatePlanDraftId();

    setRatePlanDrafts((prev) => {
      const draftNumber = prev.length + 1;
      const uniqueRatePlanName = draftNumber === 1 ? ratePlanName : `${ratePlanName} ${draftNumber}`;

      return prev.concat(
        buildDefaultRatePlanDraft({
          draftId,
          ratePlanCancellationPolicy,
          ratePlanName: uniqueRatePlanName,
        }),
      );
    });
    setExpandedRatePlanDraftIds((prev) => Array.from(new Set(prev.concat(draftId))));
    setErrorMessage(null);
  }

  function removeRatePlanDraft(draftId: string) {
    if (isRatePlanStepReadOnly || ratePlanDrafts.length <= 1) {
      return;
    }

    const removedDraft = ratePlanDrafts.find((draft) => draft.draftId === draftId);

    if (!removedDraft || isCreatedRatePlanDraft(removedDraft)) {
      return;
    }

    setRatePlanDrafts((prev) => prev.filter((draft) => draft.draftId !== draftId));
    setExpandedRatePlanDraftIds((prev) => prev.filter((id) => id !== draftId));
    setErrorMessage(null);
  }

  function toggleRatePlanDraftExpanded(draftId: string, expanded: boolean) {
    setExpandedRatePlanDraftIds((prev) =>
      expanded ? Array.from(new Set(prev.concat(draftId))) : prev.filter((id) => id !== draftId),
    );
  }

  async function createRatePlansFromDrafts() {
    const validationError = getRatePlanDraftsValidationError(ratePlanDrafts);

    if (validationError) {
      setErrorMessage(validationError);
      return false;
    }

    const pendingDrafts = ratePlanDrafts.filter((draft) => !draft.createdRatePlanId);

    if (pendingDrafts.length === 0) {
      return true;
    }

    setIsCreatingRatePlan(true);
    setErrorMessage(null);

    const createdPlans: Array<App.RatePlan> = [];
    const nextDrafts = [...ratePlanDrafts];

    try {
      for (let index = 0; index < nextDrafts.length; index += 1) {
        const draft = nextDrafts[index];

        if (draft.createdRatePlanId) {
          continue;
        }

        const response = await ReservationService.createRatePlan({
          id_salesforce_external: vendorId,
          name: draft.ratePlanName.trim(),
          cancellation_policy: draft.ratePlanCancellationPolicy.trim(),
          status: draft.ratePlanStatus.trim(),
          product_type: FLASH_RATE_PLAN_PRODUCT_TYPE,
          rate_type: draft.ratePlanRateType.trim(),
        });

        if (!response?.result?.id) {
          throw new Error('Unexpected response from createRatePlan');
        }

        nextDrafts[index] = {
          ...draft,
          createdRatePlanId: response.result.id,
        };
        createdPlans.push(response.result);
      }

      setRatePlanDrafts(nextDrafts);
      setCreatedRatePlans((prev) => buildCreatedRatePlansFromDrafts(prev, createdPlans, nextDrafts));
      return true;
    } catch (error) {
      if (createdPlans.length > 0) {
        setRatePlanDrafts(nextDrafts);
        setCreatedRatePlans((prev) => buildCreatedRatePlansFromDrafts(prev, createdPlans, nextDrafts));
      }

      reportError(error);
      const createdCount = createdPlans.length;

      setErrorMessage(
        createdCount > 0
          ? `Created ${createdCount} of ${pendingDrafts.length} rate plans. Created plans are locked — fix the remaining plan(s) and click Next to retry. ${getErrorMessage(error)}`
          : `Unable to create rate plans. ${getErrorMessage(error)}`,
      );
      return false;
    } finally {
      setIsCreatingRatePlan(false);
    }
  }

  function handleSelectRoomRatePlans(roomTypeId: string, ratePlanIds: Array<string>) {
    if (isRoomRatesStepReadOnly) {
      return;
    }

    setErrorMessage(null);
    setSelectedRoomRatePlanIds((prev) => {
      if (ratePlanIds.length === 0) {
        const next = { ...prev };
        delete next[roomTypeId];
        return next;
      }

      return { ...prev, [roomTypeId]: ratePlanIds };
    });
  }

  async function applyRoomRates() {
    if (createdRatePlans.length === 0) {
      setErrorMessage('Create at least one Flash rate plan before applying room rates.');
      return false;
    }

    const selectedRoomRateAssignments = Object.entries(selectedRoomRatePlanIds).filter(
      ([, ratePlanIds]) => ratePlanIds.length > 0,
    );

    if (selectedRoomRateAssignments.length === 0) {
      setErrorMessage('Select at least one room.');
      return false;
    }

    const propertyId = selectedProperty?.id;

    if (!propertyId) {
      setErrorMessage('Select or create a property before applying room rates.');
      return false;
    }

    setIsCreatingRoomRates(true);
    setErrorMessage(null);

    const createdRoomRates: Array<AppliedFlashRoomRate> = [];
    const appliedRoomRateIds = new Set(
      appliedFlashRoomRates.map((roomRate) => `${roomRate.roomTypeId}:${roomRate.ratePlanId}`),
    );
    const pendingRoomRateAssignments = selectedRoomRateAssignments.flatMap(([roomTypeId, ratePlanIds]) =>
      ratePlanIds
        .map((ratePlanId) => {
          const ratePlan = createdRatePlans.find((plan) => plan.id === ratePlanId);

          if (!ratePlan?.id || appliedRoomRateIds.has(`${roomTypeId}:${ratePlan.id}`)) {
            return null;
          }

          return { roomTypeId, ratePlan };
        })
        .filter((assignment): assignment is { roomTypeId: string; ratePlan: App.RatePlan } => !!assignment),
    );

    const pendingAssignmentsByRoomTypeId = new Map<string, Array<App.RatePlan>>();

    pendingRoomRateAssignments.forEach(({ roomTypeId, ratePlan }) => {
      const ratePlans = pendingAssignmentsByRoomTypeId.get(roomTypeId) ?? [];
      pendingAssignmentsByRoomTypeId.set(roomTypeId, ratePlans.concat(ratePlan));
    });

    try {
      for (const [roomTypeId, ratePlans] of pendingAssignmentsByRoomTypeId) {
        const ratePlanIds = ratePlans.map((ratePlan) => ratePlan.id);
        const response = await createBatchRoomRates.mutateAsync({
          propertyId,
          ratePlanIds,
          roomTypeId,
        });
        const allResults = response?.result;
        const ratePlanIdSet = new Set(ratePlanIds);
        const roomRateByRatePlanId = new Map<string, App.RoomRate>();

        if (Array.isArray(allResults)) {
          allResults.forEach((roomRate) => {
            const ratePlanId = roomRate?.rate_plan?.id;

            if (roomRate?.id && ratePlanId && ratePlanIdSet.has(ratePlanId) && !roomRateByRatePlanId.has(ratePlanId)) {
              roomRateByRatePlanId.set(ratePlanId, roomRate);
            }
          });
        }

        const results = ratePlanIds
          .map((ratePlanId) => roomRateByRatePlanId.get(ratePlanId))
          .filter((roomRate): roomRate is App.RoomRate => !!roomRate);

        if (!Array.isArray(allResults) || results.length !== ratePlanIds.length) {
          throw new Error('Unexpected response from createBatchRoomRates');
        }

        results.forEach((roomRate) => {
          if (!roomRate?.id) {
            throw new Error('Unexpected response from createBatchRoomRates');
          }

          const ratePlanId = roomRate.rate_plan?.id;
          const ratePlan = ratePlans.find((plan) => plan.id === ratePlanId);

          if (!ratePlanId || !ratePlan) {
            throw new Error('Unexpected response from createBatchRoomRates');
          }

          appliedRoomRateIds.add(`${roomTypeId}:${ratePlanId}`);
          createdRoomRates.push({
            ratePlanId,
            ratePlanName: ratePlan.name,
            roomName: roomRateRooms.find((room) => room.id === roomTypeId)?.name || roomTypeId,
            roomRateId: roomRate.id,
            roomTypeId,
          });
        });
      }

      setAppliedFlashRoomRates((prev) => prev.concat(createdRoomRates));
      setHasAppliedRoomRates(true);
      return true;
    } catch (error) {
      setAppliedFlashRoomRates((prev) => prev.concat(createdRoomRates));
      setSelectedRoomRatePlanIds((prev) => {
        const next = { ...prev };

        createdRoomRates.forEach(({ roomTypeId, ratePlanId }) => {
          const remainingRatePlanIds = (next[roomTypeId] ?? []).filter((id) => id !== ratePlanId);

          if (remainingRatePlanIds.length === 0) {
            delete next[roomTypeId];
          } else {
            next[roomTypeId] = remainingRatePlanIds;
          }
        });

        return next;
      });
      reportError(error);
      const createdCount = createdRoomRates.length;

      setErrorMessage(
        createdCount > 0
          ? `Applied ${createdCount} of ${pendingRoomRateAssignments.length} room rate assignments. Applied assignments are saved — fix the remaining selection and click Next to retry. ${getErrorMessage(error)}`
          : `Unable to apply rate plans. ${getErrorMessage(error)}`,
      );
      return false;
    } finally {
      setIsCreatingRoomRates(false);
    }
  }

  async function assignRoomRatesToPackages() {
    if (!selectedProperty?.id || appliedFlashRoomRates.length === 0) {
      return;
    }

    setIsAdvancingStep(true);
    setErrorMessage(null);

    try {
      const response = await OffersService.getOffer(trimmedSalesforceOfferId);
      const { assignments, unmatchedPackageNames } = buildFlashPackageRoomRateAssignments({
        offerPackages: response?.result?.packages,
        propertyId: selectedProperty.id,
        relatedPackageRooms: salesforceRooms,
        roomRates: appliedFlashRoomRates,
        roomTypes: selectedPropertyRoomTypes,
      });

      const results = await Promise.allSettled(
        assignments.map((assignment) =>
          PackagesService.updatePackage(assignment.packageData, trimmedSalesforceOfferId, assignment.packageId),
        ),
      );

      const failedUpdateCount = results.filter((result) => {
        if (result.status !== 'rejected') {
          return false;
        }

        reportError(result.reason);
        return true;
      }).length;

      if (unmatchedPackageNames.length > 0 || failedUpdateCount > 0) {
        const failedPackageCount = unmatchedPackageNames.length + failedUpdateCount;
        enqueueSnackbar(`Unable to link ${failedPackageCount} package${failedPackageCount === 1 ? '' : 's'}`, {
          variant: 'warning',
        });
      }
    } catch (error) {
      reportError(error);
      enqueueSnackbar('Unable to link packages to rooms', { variant: 'warning' });
    } finally {
      setIsAdvancingStep(false);
    }
  }

  async function handleNext() {
    if (isStepBusy) {
      return;
    }

    if (activeStep === 0 && !isSalesforceOpportunityId(trimmedSalesforceOfferId)) {
      setErrorMessage('Enter a valid Salesforce offer ID.');
      return;
    }

    if (activeStep === 0) {
      const runId = validationRunId.current + 1;
      validationRunId.current = runId;
      setIsValidatingOpportunity(true);
      const { data: opportunity, error } = await refetchOpportunity();

      if (runId !== validationRunId.current) {
        return;
      }

      if (error || !opportunity?.account_18_digit_id__c) {
        setIsValidatingOpportunity(false);
        setErrorMessage(`Unable to validate the Salesforce offer ID. ${getErrorMessage(error)}`);
        return;
      }

      const opportunityVendorId = opportunity.account_18_digit_id__c;

      if (opportunityVendorId !== vendorId) {
        setIsValidatingOpportunity(false);
        setErrorMessage('This Salesforce offer does not belong to this vendor.');
        return;
      }

      const cancellationPolicy =
        mapSFCancellationPolicyToAdmin(opportunity.functional_details?.cancellation_policy__c ?? undefined) ?? '';
      const { data: opportunityRelated } = await refetchOpportunityRelated().catch((error) => {
        reportError(error);
        return { data: undefined };
      });

      if (runId !== validationRunId.current) {
        return;
      }

      const travelStartRatePlanName = getFlashRatePlanNameFromTravelStart(opportunityRelated?.deals);
      initializeRatePlanDrafts({
        ratePlanCancellationPolicy: cancellationPolicy,
        ratePlanName: travelStartRatePlanName || getDefaultFlashRatePlanName(),
      });

      setPropertyFormData({
        id_salesforce_external: vendorId,
        ...mapVendorBillingToPropertyFormData(vendor),
        logo_id_cloudinary_external: DEFAULT_NEW_PROPERTY_LOGO_ID,
        ...mapRedemptionOptionsToPropertyFormData(opportunity.key_commercial?.redemption_options__c),
        ...mapExtraGuestSurchargeAgeRangesToPropertyFormData(opportunityRelated?.extra_guest_surcharges),
      });
      setSalesforceRooms(opportunityRelated?.deals ?? opportunityRelated?.rooms ?? []);
      setRoomDrafts([]);
      setIsValidatingOpportunity(false);
    }

    if (activeStep === 1 && isCreatingNewProperty) {
      return;
    }

    if (activeStep === 1 && isLoadingPropertyForm) {
      setErrorMessage('Wait for property details to finish loading.');
      return;
    }

    if (activeStep === 1 && missingRequiredPropertyFields.length > 0) {
      setErrorMessage(`Complete mandatory property fields: ${missingRequiredPropertyFields.join(', ')}.`);
      return;
    }

    if (activeStep === 1 && isCreatingProperty && !propertySchema) {
      setErrorMessage('Unable to validate property details.');
      return;
    }

    if (activeStep === 1 && !isCreatingProperty) {
      setIsAdvancingStep(true);

      try {
        const preparedRoomDrafts = await prepareRoomDrafts(selectedProperty, selectedPropertyRoomTypes);

        if (selectedPropertyHasRooms && preparedRoomDrafts.length === 0) {
          setErrorMessage(null);
          setActiveStep(3);
          return;
        }
      } finally {
        setIsAdvancingStep(false);
      }
    }

    if (activeStep === 2) {
      const roomsCreated = await createSelectedRooms();

      if (!roomsCreated) {
        return;
      }
    }

    if (activeStep === 3) {
      const ratePlansCreated = await createRatePlansFromDrafts();

      if (!ratePlansCreated) {
        return;
      }
    }

    if (activeStep === 4 && !hasAppliedRoomRates) {
      const roomRatesCreated = await applyRoomRates();

      if (!roomRatesCreated) {
        return;
      }
    }

    if (activeStep === 5) {
      await assignRoomRatesToPackages();
    }

    const shouldPreserveRoomDraftWarning = activeStep === 1 && !isCreatingProperty && !selectedPropertyHasRooms;
    if (!shouldPreserveRoomDraftWarning) {
      setErrorMessage(null);
    }
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  }

  return (
    <>
      <Button variant="contained" onClick={handleOpen} disabled={disabled}>
        Set up Flash offer
      </Button>

      <Dialog open={isDialogOpen} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center' }}>
          Set up Flash offer
          <IconButton
            aria-label="Close"
            disabled={isStepBusy}
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map((step) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Stack spacing={2}>
            <Typography variant="h6">{currentStep.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {currentStep.helperText}
            </Typography>

            {activeStep === 0 && (
              <TextField
                label="Salesforce offer ID"
                value={salesforceOfferId}
                onChange={(event) => {
                  setSalesforceOfferId(event.target.value);
                  initializeRatePlanDrafts();
                  setErrorMessage(null);
                }}
                required
                fullWidth
                autoFocus
                disabled={isStepBusy}
              />
            )}

            {activeStep === 1 && (
              <PropertyStep
                activeProperties={activeProperties}
                hasPropertyFormError={hasPropertyFormError}
                isBusy={isStepBusy}
                isCreatingProperty={isCreatingProperty}
                isLoadingPropertyForm={isLoadingPropertyForm}
                onDraftChange={setPropertyFormData}
                onPropertyCreateError={handlePropertyCreateError}
                onPropertyCreateStart={handlePropertyCreateStart}
                onPropertyCreateSuccess={handlePropertyCreateSuccess}
                onPropertySelect={(propertyId) => {
                  setErrorMessage(null);
                  setSelectedPropertyId(propertyId);
                  setRoomDrafts([]);
                }}
                onRetryPropertySchema={() => {
                  setErrorMessage(null);
                  refetchCreatePropertySchema();
                }}
                propertyFormData={propertyFormData}
                propertySchema={propertySchema}
                selectedPropertyId={selectedPropertyId}
                vendorId={vendorId}
              />
            )}

            {isRatePlanStep && (
              <RatePlanStep
                disabled={isStepBusy}
                expandedRatePlanDraftIds={expandedRatePlanDraftIds}
                hasSchemaError={hasRatePlanCreateSchemaError}
                isLoadingSchema={isLoadingRatePlanCreateSchema}
                onAddDraft={addRatePlanDraft}
                onClearError={() => setErrorMessage(null)}
                onRemoveDraft={removeRatePlanDraft}
                onRetrySchema={refetchRatePlanCreateSchema}
                onToggleDraftExpanded={toggleRatePlanDraftExpanded}
                onUpdateDraft={updateRatePlanDraft}
                ratePlanCreateSchema={ratePlanCreateSchema}
                ratePlanDrafts={ratePlanDrafts}
                readOnly={isRatePlanStepReadOnly}
              />
            )}

            {activeStep === 2 && (
              <RoomsStep
                amenityOptions={amenityOptions}
                hasRoomDraftFormError={hasRoomDraftFormError}
                isBusy={isStepBusy}
                isLoadingRoomDraftForm={isLoadingRoomDraftForm}
                isLoadingRoomTypesSchema={isLoadingRoomTypesSchema}
                onAddAmenityOption={handleAddAmenityOption}
                onAddRoomDraft={handleAddRoomDraft}
                onRemoveRoomDraft={handleRemoveRoomDraft}
                onRetryLoadRoomFields={() => {
                  setErrorMessage(null);
                  refetchRoomTypesSchema();
                }}
                onUpdateRoomDraft={updateRoomDraft}
                property={selectedProperty}
                roomDrafts={roomDrafts}
                roomTypesFormSchema={roomTypesFormSchema}
              />
            )}

            {isRoomRatesStep && (
              <RoomRatesStep
                appliedRatePlanNamesByRoomTypeId={appliedRatePlanNamesByRoomTypeId}
                createdRatePlans={createdRatePlans}
                disabled={isStepBusy}
                onClearRooms={() => {
                  if (isRoomRatesStepReadOnly) {
                    return;
                  }

                  setErrorMessage(null);
                  setSelectedRoomRatePlanIds({});
                }}
                onSelectAllRooms={() => {
                  if (isRoomRatesStepReadOnly) {
                    return;
                  }

                  setErrorMessage(null);
                  const defaultRatePlanIds = createdRatePlanIds;
                  setSelectedRoomRatePlanIds(
                    defaultRatePlanIds.length > 0
                      ? Object.fromEntries(
                          selectableRoomRateRoomTypeIds.map((roomTypeId) => [roomTypeId, defaultRatePlanIds]),
                        )
                      : {},
                  );
                }}
                onSelectRatePlans={handleSelectRoomRatePlans}
                readOnly={isRoomRatesStepReadOnly}
                rooms={roomRateRooms}
                selectedRatePlanIds={selectedRoomRatePlanIds}
              />
            )}

            {isTaxesAndFeesStep && (
              <TaxesFeesStep
                disabled={isStepBusy}
                formId={TAXES_FEES_FORM_ID}
                onSaved={async () => {
                  setErrorMessage(null);
                  await assignRoomRatesToPackages();
                  setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
                }}
                onSavingChange={setIsSavingTaxesFees}
                propertyId={selectedProperty?.id}
              />
            )}

            {isContentStep && contentPageUrl && (
              <Alert severity="info">
                Redirecting to the{' '}
                <Link component="button" onClick={goToContentPage} underline="hover">
                  Offer Builder CM Checklist page
                </Link>
                . If it does not open automatically, use the button below.
              </Alert>
            )}

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <WizardActions
          activeStep={activeStep}
          contentPageUrl={contentPageUrl}
          isContentStep={isContentStep}
          isCreatePropertyStep={isCreatePropertyStep}
          isPropertyStepNextDisabled={isPropertyStepNextDisabled}
          isRatePlanStep={isRatePlanStep}
          isRatePlanStepNextDisabled={isRatePlanStepNextDisabled}
          isRoomRatesStepNextDisabled={isRoomRatesStepNextDisabled}
          isRoomStepNextDisabled={isRoomStepNextDisabled}
          isStepBusy={isStepBusy}
          isTaxesAndFeesStep={isTaxesAndFeesStep}
          onGoToContentPage={goToContentPage}
          onNext={handleNext}
          onPrevious={handlePrevious}
          primaryButtonLabel={primaryButtonLabel}
          trimmedSalesforceOfferId={trimmedSalesforceOfferId}
        />
      </Dialog>

      <ExitConfirmationDialog
        onCancel={handleCancelClose}
        onConfirm={handleConfirmClose}
        open={isExitConfirmationOpen}
      />
    </>
  );
}
